<template>
  <div class="supply-efficiency-panel">
    <div class="eff-goal" role="note">
      <strong>{{ '목표' | translate }}</strong>
      <p>
        {{
          '토너·드럼에 정품 또는 재생을 지정하세요. 정품과 재생의 출력효율(장/회)을 비교하는 기준이 됩니다. 유형이 비어 있으면 비교가 표시되지 않습니다.'
            | translate
        }}
      </p>
      <p v-if="includeRental">
        {{
          '유형은 ERP 소모품 현황의 매핑·실제 장착에서 지정합니다. 지정 후 다음 일별 배치부터 이 화면에 반영됩니다.'
            | translate
        }}
      </p>
      <p v-else>
        {{
          '소모품 현황에서 매핑하기 또는 실제 장착으로 지정하세요. 지정 후 다음 일별 배치부터 이 화면에 반영됩니다.'
            | translate
        }}
      </p>
    </div>
    <p class="eff-hint">
      {{
        '교체 구간 A4 환산 출력량(장/회) 기준입니다. 배치 스냅샷·사이클 팩트가 없으면 비교가 NA로 표시됩니다. 랭킹 행을 클릭하면 개별 사이클을 확인할 수 있습니다.'
          | translate
      }}
    </p>

    <div class="eff-kpi-row">
      <div class="eff-kpi">
        <div class="label">{{ '총 A4 출력' | translate }}</div>
        <div class="value">{{ formatNum(overview.totalPagesA4) }}</div>
      </div>
      <div class="eff-kpi">
        <div class="label">{{ '평균 출력효율(장/회)' | translate }}</div>
        <div class="value">
          {{ formatNum(overview.avgPagesPerCycleA4) }}
          <em
            v-if="badge(overview.efficiencyCompare)"
            class="compare-badge"
            :class="'compare-' + String(overview.efficiencyCompare || '').toLowerCase()"
            :title="overviewTooltip"
          >{{ badge(overview.efficiencyCompare) }}</em>
        </div>
      </div>
      <div class="eff-kpi">
        <div class="label">{{ '평균주기(일)' | translate }}</div>
        <div class="value">{{ formatNum(overview.avgCycleDays) }}</div>
      </div>
      <div class="eff-kpi">
        <div class="label">{{ '사이클수' | translate }}</div>
        <div class="value">{{ formatInt(overview.cycleCount) }}</div>
      </div>
      <div class="eff-kpi">
        <div class="label">{{ '프린터수' | translate }}</div>
        <div class="value">{{ formatInt(overview.printerCount) }}</div>
      </div>
    </div>

    <div class="eff-rank-tabs">
      <button
        v-for="tab in rankTabs"
        :key="tab.value"
        type="button"
        :class="{ active: rankGroupBy === tab.value }"
        @click="changeRankGroup(tab.value)"
      >
        {{ tab.label | translate }}
      </button>
    </div>

    <div class="table-wrap">
      <table class="supply-table">
        <thead>
          <tr>
            <th class="sortable" @click="sortBy('groupLabel')">
              {{ rankLabel | translate }}{{ sortMark('groupLabel') }}
            </th>
            <th v-if="rankGroupBy === 'SUPPLY'">{{ '유형' | translate }}</th>
            <th v-if="rankGroupBy === 'SUPPLY'">{{ '구분' | translate }}</th>
            <th class="sortable" @click="sortBy('pagesPerCycleA4')">
              {{ '출력효율(장/회)' | translate }}{{ sortMark('pagesPerCycleA4') }}
            </th>
            <th class="sortable" @click="sortBy('totalPagesA4')">
              {{ '총 A4' | translate }}{{ sortMark('totalPagesA4') }}
            </th>
            <th class="sortable" @click="sortBy('avgCycleDays')">
              {{ '평균주기' | translate }}{{ sortMark('avgCycleDays') }}
            </th>
            <th class="sortable" @click="sortBy('cycleCount')">
              {{ '사이클' | translate }}{{ sortMark('cycleCount') }}
            </th>
            <th class="sortable" @click="sortBy('printerCount')">
              {{ '프린터' | translate }}{{ sortMark('printerCount') }}
            </th>
            <th v-if="rankGroupBy === 'SUPPLY'" class="sortable" @click="sortBy('efficiencyDiffPct')">
              {{ '전체대비' | translate }}{{ sortMark('efficiencyDiffPct') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!rows.length">
            <td :colspan="colspan" class="empty">
              {{
                '데이터가 없습니다. 토너·드럼에 정품 또는 재생을 지정하면 출력효율 비교가 쌓입니다.'
                  | translate
              }}
            </td>
          </tr>
          <tr
            v-for="(row, idx) in rows"
            :key="'eff-' + idx"
            class="rank-row"
            :class="{ selected: isSelectedRank(row) }"
            @click="openCycles(row)"
          >
            <td :title="row.groupLabel">{{ row.groupLabel || row.partNumber || '-' }}</td>
            <td v-if="rankGroupBy === 'SUPPLY'">{{ formatCompat(row.compatibilityType) }}</td>
            <td v-if="rankGroupBy === 'SUPPLY'">
              {{ formatCategory(row.supplyCategory) }} {{ row.colorCode || '' }}
            </td>
            <td>
              <span class="metric-with-compare" :title="rowTooltip(row)">
                {{ formatNum(row.pagesPerCycleA4) }}
                <em
                  v-if="rankGroupBy === 'SUPPLY' && badge(row.efficiencyCompare)"
                  class="compare-badge"
                  :class="'compare-' + String(row.efficiencyCompare || '').toLowerCase()"
                >{{ badge(row.efficiencyCompare) }}</em>
              </span>
            </td>
            <td>{{ formatNum(row.totalPagesA4) }}</td>
            <td>{{ formatNum(row.avgCycleDays) }}</td>
            <td>{{ formatInt(row.cycleCount) }}</td>
            <td>{{ formatInt(row.printerCount) }}</td>
            <td v-if="rankGroupBy === 'SUPPLY'">
              {{
                row.efficiencyDiffPct == null
                  ? '-'
                  : ((row.efficiencyDiffPct > 0 ? '+' : '') + Number(row.efficiencyDiffPct).toLocaleString() + '%')
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="supply-paging">
      <button type="button" class="supply-btn outline" :disabled="page <= 1" @click="prevPage">
        {{ '이전' | translate }}
      </button>
      <span>{{ page }} / {{ totalPages }}</span>
      <button
        type="button"
        class="supply-btn outline"
        :disabled="page >= totalPages"
        @click="nextPage"
      >
        {{ '다음' | translate }}
      </button>
    </div>

    <div v-if="cycleOpen" class="cycle-drilldown">
      <div class="cycle-header">
        <div>
          <strong>{{ '사이클 상세' | translate }}</strong>
          <span class="cycle-label">{{ cycleLabel }}</span>
        </div>
        <button type="button" class="supply-btn outline" @click="closeCycles">
          {{ '닫기' | translate }}
        </button>
      </div>
      <div class="table-wrap">
        <table class="supply-table">
          <thead>
            <tr>
              <th class="sortable" @click="sortCyclesBy('cycleEndDate')">
                {{ '교체일' | translate }}{{ cycleSortMark('cycleEndDate') }}
              </th>
              <th class="sortable" @click="sortCyclesBy('cycleStartDate')">
                {{ '시작일' | translate }}{{ cycleSortMark('cycleStartDate') }}
              </th>
              <th class="sortable" @click="sortCyclesBy('cycleDays')">
                {{ '주기(일)' | translate }}{{ cycleSortMark('cycleDays') }}
              </th>
              <th class="sortable" @click="sortCyclesBy('pagesA4Delta')">
                {{ 'A4 출력' | translate }}{{ cycleSortMark('pagesA4Delta') }}
              </th>
              <th>{{ '프린터' | translate }}</th>
              <th>{{ '모델' | translate }}</th>
              <th>{{ '거래처' | translate }}</th>
              <th v-if="includeRental">{{ '임대업체' | translate }}</th>
              <th>{{ '소모품' | translate }}</th>
              <th>{{ '유형' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!cycleRows.length">
              <td :colspan="cycleColspan" class="empty">{{ '데이터가 없습니다.' | translate }}</td>
            </tr>
            <tr v-for="(c, i) in cycleRows" :key="'cyc-' + (c.endHistId || i)">
              <td>{{ c.cycleEndDate || '-' }}</td>
              <td>{{ c.cycleStartDate || '-' }}</td>
              <td>{{ formatInt(c.cycleDays) }}</td>
              <td>{{ formatNum(c.pagesA4Delta) }}</td>
              <td>{{ c.printerSerialNo || '-' }}</td>
              <td>{{ c.printerModelName || '-' }}</td>
              <td>{{ c.accountName || '-' }}</td>
              <td v-if="includeRental">{{ c.rentalcompanyName || '-' }}</td>
              <td>{{ c.partNumber || c.identityKey || '-' }}</td>
              <td>{{ formatCompat(c.compatibilityType) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="supply-paging">
        <button
          type="button"
          class="supply-btn outline"
          :disabled="cyclePage <= 1"
          @click="prevCyclePage"
        >
          {{ '이전' | translate }}
        </button>
        <span>{{ cyclePage }} / {{ cycleTotalPages }}</span>
        <button
          type="button"
          class="supply-btn outline"
          :disabled="cyclePage >= cycleTotalPages"
          @click="nextCyclePage"
        >
          {{ '다음' | translate }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SupplyEfficiencyPanel',
  props: {
    /** 부모 searchPayload() 결과 또는 동일 형태 객체 */
    basePayload: { type: Object, required: true },
    overviewPath: { type: String, required: true },
    rankingPath: { type: String, required: true },
    cyclesPath: { type: String, required: true },
    /** Admin이면 RENTAL 탭 포함 */
    includeRental: { type: Boolean, default: false },
  },
  data() {
    return {
      overview: {},
      rows: [],
      totalCount: 0,
      page: 1,
      rowsPerPage: 30,
      rankGroupBy: 'SUPPLY',
      orderBy: 'pagesPerCycleA4',
      orderDir: 'DESC',
      loaded: false,
      cycleOpen: false,
      cycleLabel: '',
      cycleFilter: null,
      cycleRows: [],
      cycleTotalCount: 0,
      cyclePage: 1,
      cycleRowsPerPage: 20,
      cycleOrderBy: 'cycleEndDate',
      cycleOrderDir: 'DESC',
    }
  },
  computed: {
    rankTabs() {
      const tabs = [
        { value: 'SUPPLY', label: '소모품' },
        { value: 'PRINTER', label: '프린터' },
        { value: 'ACCOUNT', label: '거래처' },
        { value: 'MODEL', label: '모델' },
      ]
      if (this.includeRental) {
        tabs.splice(3, 0, { value: 'RENTAL', label: '임대업체' })
      }
      return tabs
    },
    rankLabel() {
      const map = {
        SUPPLY: '소모품',
        PRINTER: '프린터',
        ACCOUNT: '거래처',
        MODEL: '모델',
        RENTAL: '임대업체',
      }
      return map[this.rankGroupBy] || '구분'
    },
    totalPages() {
      return Math.max(1, Math.ceil((this.totalCount || 0) / this.rowsPerPage))
    },
    cycleTotalPages() {
      return Math.max(1, Math.ceil((this.cycleTotalCount || 0) / this.cycleRowsPerPage))
    },
    colspan() {
      return this.rankGroupBy === 'SUPPLY' ? 9 : 6
    },
    cycleColspan() {
      return this.includeRental ? 10 : 9
    },
    overviewTooltip() {
      const o = this.overview || {}
      if (!o.efficiencyCompare || o.efficiencyCompare === 'NA') {
        return this.t('전체 벤치마크 비교 불가 (샘플 부족·배치 미실행)')
      }
      const local = this.formatNum(o.avgPagesPerCycleA4)
      const global = this.formatNum(o.globalAvgPagesPerCycleA4)
      const pct =
        o.efficiencyDiffPct == null
          ? ''
          : ` (${o.efficiencyDiffPct > 0 ? '+' : ''}${Number(o.efficiencyDiffPct).toLocaleString()}%)`
      const date = o.benchmarkStatDate ? ` · ${o.benchmarkStatDate}` : ''
      return `${this.t('당사')} ${local} / ${this.t('전체')} ${global}${pct}${date}`
    },
  },
  watch: {
    basePayload: {
      deep: true,
      handler() {
        this.page = 1
        this.closeCycles()
        this.reload()
      },
    },
  },
  mounted() {
    this.reload()
  },
  methods: {
    t(s) {
      return this.$options.filters?.translate ? this.$options.filters.translate(s) : s
    },
    payload() {
      return {
        ...this.basePayload,
        groupBy: this.rankGroupBy,
        orderBy: this.orderBy,
        orderDir: this.orderDir,
        page: this.page,
        rows: this.rowsPerPage,
      }
    },
    cyclePayload() {
      return {
        ...this.basePayload,
        ...(this.cycleFilter || {}),
        orderBy: this.cycleOrderBy,
        orderDir: this.cycleOrderDir,
        page: this.cyclePage,
        rows: this.cycleRowsPerPage,
      }
    },
    async reload() {
      await Promise.all([this.loadOverview(), this.loadRanking()])
      this.loaded = true
    },
    async loadOverview() {
      try {
        const res = await this.$request(this.overviewPath).setObject(this.payload()).enqueue()
        this.overview = res?.data || res || {}
      } catch (e) {
        console.error(e)
        this.overview = {}
      }
    },
    async loadRanking() {
      try {
        const res = await this.$request(this.rankingPath).setObject(this.payload()).enqueue()
        const data = res?.data || res || {}
        this.rows = Array.isArray(data.list) ? data.list : []
        this.totalCount = Number(data.totalCount || 0)
      } catch (e) {
        console.error(e)
        this.rows = []
        this.totalCount = 0
      }
    },
    async loadCycles() {
      if (!this.cycleOpen || !this.cycleFilter) return
      try {
        const res = await this.$request(this.cyclesPath).setObject(this.cyclePayload()).enqueue()
        const data = res?.data || res || {}
        this.cycleRows = Array.isArray(data.list) ? data.list : []
        this.cycleTotalCount = Number(data.totalCount || 0)
      } catch (e) {
        console.error(e)
        this.cycleRows = []
        this.cycleTotalCount = 0
      }
    },
    buildCycleFilter(row) {
      const filter = {
        groupKey: null,
        compatibilityType: null,
        monitoringPrinterId: null,
        accountId: null,
        printerModelId: null,
      }
      if (this.includeRental) {
        filter.rentalcompanyId = this.basePayload?.rentalcompanyId || null
      }
      if (this.rankGroupBy === 'SUPPLY') {
        filter.groupKey = row.groupKey || null
        filter.compatibilityType = row.compatibilityType || null
      } else if (this.rankGroupBy === 'PRINTER') {
        filter.monitoringPrinterId = row.monitoringPrinterId || null
      } else if (this.rankGroupBy === 'ACCOUNT') {
        filter.accountId = row.accountId || null
      } else if (this.rankGroupBy === 'MODEL') {
        filter.printerModelId = row.printerModelId || null
      } else if (this.rankGroupBy === 'RENTAL') {
        filter.rentalcompanyId = row.rentalcompanyId || null
      }
      return filter
    },
    openCycles(row) {
      this.cycleFilter = this.buildCycleFilter(row)
      this.cycleLabel = row.groupLabel || row.partNumber || '-'
      this.cyclePage = 1
      this.cycleOrderBy = 'cycleEndDate'
      this.cycleOrderDir = 'DESC'
      this.cycleOpen = true
      this.loadCycles()
    },
    closeCycles() {
      this.cycleOpen = false
      this.cycleFilter = null
      this.cycleLabel = ''
      this.cycleRows = []
      this.cycleTotalCount = 0
      this.cyclePage = 1
    },
    isSelectedRank(row) {
      if (!this.cycleOpen || !this.cycleFilter) return false
      const f = this.cycleFilter
      if (this.rankGroupBy === 'SUPPLY') {
        return f.groupKey === row.groupKey && f.compatibilityType === row.compatibilityType
      }
      if (this.rankGroupBy === 'PRINTER') return f.monitoringPrinterId === row.monitoringPrinterId
      if (this.rankGroupBy === 'ACCOUNT') return f.accountId === row.accountId
      if (this.rankGroupBy === 'MODEL') return f.printerModelId === row.printerModelId
      if (this.rankGroupBy === 'RENTAL') return f.rentalcompanyId === row.rentalcompanyId
      return false
    },
    changeRankGroup(v) {
      this.rankGroupBy = v
      this.page = 1
      this.orderBy = 'pagesPerCycleA4'
      this.orderDir = 'DESC'
      this.closeCycles()
      this.reload()
    },
    sortBy(col) {
      if (this.orderBy === col) {
        this.orderDir = this.orderDir === 'ASC' ? 'DESC' : 'ASC'
      } else {
        this.orderBy = col
        this.orderDir = 'DESC'
      }
      this.page = 1
      this.loadRanking()
    },
    sortCyclesBy(col) {
      if (this.cycleOrderBy === col) {
        this.cycleOrderDir = this.cycleOrderDir === 'ASC' ? 'DESC' : 'ASC'
      } else {
        this.cycleOrderBy = col
        this.cycleOrderDir = 'DESC'
      }
      this.cyclePage = 1
      this.loadCycles()
    },
    sortMark(col) {
      if (this.orderBy !== col) return ''
      return this.orderDir === 'ASC' ? ' ▲' : ' ▼'
    },
    cycleSortMark(col) {
      if (this.cycleOrderBy !== col) return ''
      return this.cycleOrderDir === 'ASC' ? ' ▲' : ' ▼'
    },
    prevPage() {
      if (this.page > 1) {
        this.page -= 1
        this.loadRanking()
      }
    },
    nextPage() {
      if (this.page < this.totalPages) {
        this.page += 1
        this.loadRanking()
      }
    },
    prevCyclePage() {
      if (this.cyclePage > 1) {
        this.cyclePage -= 1
        this.loadCycles()
      }
    },
    nextCyclePage() {
      if (this.cyclePage < this.cycleTotalPages) {
        this.cyclePage += 1
        this.loadCycles()
      }
    },
    formatNum(v) {
      if (v === null || v === undefined || v === '') return '-'
      return Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 })
    },
    formatInt(v) {
      if (v === null || v === undefined || v === '') return '-'
      return Number(v).toLocaleString()
    },
    badge(compare) {
      const key = String(compare || '').toUpperCase()
      if (key === 'HIGHER') return this.t('높음')
      if (key === 'LOWER') return this.t('낮음')
      if (key === 'SIMILAR') return this.t('유사')
      return ''
    },
    rowTooltip(row) {
      if (!row.efficiencyCompare || row.efficiencyCompare === 'NA') {
        return this.t('전체 벤치마크 비교 불가 (유형 미지정·샘플 부족·배치 미실행)')
      }
      const local = this.formatNum(row.pagesPerCycleA4)
      const global = this.formatNum(row.globalPagesPerCycleA4)
      const pct =
        row.efficiencyDiffPct == null
          ? ''
          : ` (${row.efficiencyDiffPct > 0 ? '+' : ''}${Number(row.efficiencyDiffPct).toLocaleString()}%)`
      const date = row.benchmarkStatDate ? ` · ${row.benchmarkStatDate}` : ''
      return `${this.t('당사')} ${local} / ${this.t('전체')} ${global}${pct}${date}`
    },
    formatCompat(v) {
      const key = String(v || '').toUpperCase()
      if (key === 'OEM' || key === 'EXACT' || key === 'GENUINE') return this.t('정품')
      if (key === 'COMPATIBLE' || key === 'COMPAT') return this.t('호환')
      if (key === 'REMAN' || key === 'REMANUFACTURED' || key === 'REFILL') return this.t('재생')
      return this.t('미지정')
    },
    formatCategory(cat) {
      const map = {
        TONER: this.t('토너'),
        DRUM: this.t('드럼'),
        WASTE: this.t('폐토너'),
        OTHER: this.t('기타'),
      }
      return map[cat] || cat || '-'
    },
  },
}
</script>

<style scoped>
.supply-efficiency-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.eff-goal {
  margin: 0;
  padding: 10px 12px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #eff6ff;
  color: #1e3a8a;
  font-size: 13px;
  line-height: 1.5;
}
.eff-goal strong {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
}
.eff-goal p {
  margin: 0;
}
.eff-goal p + p {
  margin-top: 4px;
  color: #1e40af;
}
.eff-hint {
  margin: 0;
  color: #64748b;
  font-size: 12px;
}
.eff-kpi-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 8px;
}
.eff-kpi {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff;
}
.eff-kpi .label {
  font-size: 12px;
  color: #64748b;
}
.eff-kpi .value {
  margin-top: 4px;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}
.eff-rank-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.eff-rank-tabs button {
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 16px;
  padding: 4px 12px;
  cursor: pointer;
  font-size: 13px;
}
.eff-rank-tabs button.active {
  border-color: #2563eb;
  color: #2563eb;
  background: #eff6ff;
}
.table-wrap {
  overflow: auto;
}
.rank-row {
  cursor: pointer;
}
.rank-row:hover {
  background: #f8fafc;
}
.rank-row.selected {
  background: #eff6ff;
}
.compare-badge {
  font-style: normal;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 10px;
}
.compare-higher {
  background: #dcfce7;
  color: #166534;
}
.compare-lower {
  background: #fee2e2;
  color: #991b1b;
}
.compare-similar {
  background: #e2e8f0;
  color: #475569;
}
.metric-with-compare {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.empty {
  text-align: center;
  color: #94a3b8;
  padding: 24px;
}
.supply-paging {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
}
.cycle-drilldown {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.cycle-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.cycle-label {
  margin-left: 8px;
  color: #475569;
  font-size: 13px;
}
</style>
